import React, { useEffect, useState } from 'react';
import {
  Image,
  KeyboardAvoidingView,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { PrimaryCTA } from '../../../components';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { selectUser } from '../../auth/authSlice';
import { fpColors } from '../../../styles/theme';
import {
  clearError,
  closeShareModal,
  createShare,
  selectSharingError,
  selectSharingLoading,
  selectShareModal,
} from '../sharingSlice';

/**
 * Share Item screen — full-screen modal mirroring Stitch share_item_1.
 *
 * Layout:
 *   - Top bar: back arrow + "Share Item" (purple) + avatar
 *   - Item preview card: image + "Sharing" label + name + location
 *   - Recipient email input + short helper
 *   - Cancel + Send Invitation footer
 *
 * Backend mapping: shares are created as `sharing_type: 'BORROW'`. The old
 * View/Borrow/Full access tiers were removed — they enforced nothing (the
 * API has no `permission_level`); they only wrote a cosmetic notes prefix.
 * Sharing is now a single decision: enter an email and send.
 */
export const ShareModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const shareModal = useAppSelector(selectShareModal);
  const loading = useAppSelector(selectSharingLoading);
  const error = useAppSelector(selectSharingError);
  const user = useAppSelector(selectUser);

  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!shareModal.isOpen) {
      setEmail('');
      setLocalError(null);
      dispatch(clearError());
    }
  }, [shareModal.isOpen, dispatch]);

  const close = () => dispatch(closeShareModal());

  const handleSend = async () => {
    setLocalError(null);
    if (!shareModal.itemId) {
      setLocalError('No item selected.');
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setLocalError("Please enter the recipient's email.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setLocalError('Please enter a valid email address.');
      return;
    }

    try {
      await dispatch(
        createShare({
          itemId: shareModal.itemId,
          request: {
            sharing_type: 'BORROW',
            expires_in_hours: 168,
            notify_email: trimmedEmail,
          },
        }),
      ).unwrap();
      Toast.show({
        type: 'success',
        text1: 'Invitation sent',
        text2: `Shared with ${trimmedEmail}`,
      });
      close();
    } catch (e: any) {
      setLocalError(e?.message || 'Something went wrong.');
    }
  };

  const displayError =
    localError || (error && typeof error === 'string' ? error : null);

  const initials = (user?.full_name || 'U')
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <Modal
      visible={shareModal.isOpen}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={close}
    >
      <StatusBar barStyle="dark-content" />
      <View style={styles.page}>
        {/* Top bar */}
        <View style={styles.topBar}>
          <View style={styles.topBarInner}>
            <Pressable
              onPress={close}
              hitSlop={8}
              style={styles.backBtn}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <MaterialIcons
                name="arrow-back"
                size={22}
                color={fpColors.onSurface}
              />
            </Pressable>
            <Text style={styles.topTitle}>Share Item</Text>
            <View style={styles.avatar}>
              <Text style={styles.avatarText}>{initials}</Text>
            </View>
          </View>
        </View>

        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          style={{ flex: 1 }}
        >
          <ScrollView
            contentContainerStyle={styles.scroll}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {/* Item preview card */}
            <View style={styles.itemCard}>
              <View style={styles.itemImageWrap}>
                {shareModal.itemImageUrl ? (
                  <Image
                    source={{ uri: shareModal.itemImageUrl }}
                    style={styles.itemImage}
                  />
                ) : (
                  <View style={[styles.itemImage, styles.itemImageFallback]}>
                    <MaterialIcons
                      name="image"
                      size={28}
                      color={fpColors.outline}
                    />
                  </View>
                )}
              </View>
              <View style={styles.itemBody}>
                <Text style={styles.itemKicker}>Sharing</Text>
                <Text style={styles.itemName} numberOfLines={2}>
                  {shareModal.itemName || 'Item'}
                </Text>
                {shareModal.itemLocation ? (
                  <View style={styles.itemLocation}>
                    <MaterialIcons
                      name="place"
                      size={14}
                      color={fpColors.primary}
                    />
                    <Text style={styles.itemLocationText} numberOfLines={1}>
                      {shareModal.itemLocation}
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>

            {/* Recipient email */}
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>Invite by email</Text>
              <View style={styles.emailInputWrap}>
                <MaterialIcons
                  name="mail"
                  size={20}
                  color={fpColors.onSurfaceVariant}
                />
                <TextInput
                  value={email}
                  onChangeText={setEmail}
                  placeholder="email@example.com"
                  placeholderTextColor={`${fpColors.outline}99`}
                  autoCapitalize="none"
                  autoComplete="email"
                  keyboardType="email-address"
                  style={styles.emailInput}
                  onSubmitEditing={handleSend}
                  returnKeyType="send"
                />
              </View>
              <Text style={styles.helperText}>
                They'll get an email with a link to view this item and its
                details.
              </Text>
            </View>

            {displayError ? (
              <View style={styles.errorBanner}>
                <MaterialIcons
                  name="error-outline"
                  size={18}
                  color={fpColors.error}
                />
                <Text style={styles.errorText}>{displayError}</Text>
              </View>
            ) : null}
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <Pressable
              onPress={close}
              style={({ pressed }) => [
                styles.cancelBtn,
                pressed && { opacity: 0.7 },
              ]}
              accessibilityRole="button"
            >
              <Text style={styles.cancelLabel}>Cancel</Text>
            </Pressable>
            <View style={{ flex: 1 }}>
              <PrimaryCTA
                label={loading.creating ? 'Sending…' : 'Send Invitation'}
                onPress={handleSend}
                loading={loading.creating}
                iconRight={loading.creating ? null : 'send'}
              />
            </View>
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  page: { flex: 1, backgroundColor: fpColors.background },
  topBar: {
    backgroundColor: fpColors.surfaceContainerLowest,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
    paddingTop: Platform.OS === 'ios' ? 48 : 12,
  },
  topBarInner: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    gap: 8,
  },
  backBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  topTitle: {
    flex: 1,
    fontFamily: 'Inter_700Bold',
    fontSize: 20,
    color: fpColors.primary,
  },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: fpColors.primaryContainer,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onPrimaryContainer,
    letterSpacing: 0.5,
  },
  scroll: {
    padding: 20,
    gap: 20,
    paddingBottom: 30,
  },
  itemCard: {
    flexDirection: 'row',
    gap: 14,
    padding: 14,
    borderRadius: 16,
    backgroundColor: `${fpColors.primary}0f`,
    borderWidth: 1,
    borderColor: `${fpColors.primary}1f`,
    alignItems: 'center',
  },
  itemImageWrap: {
    width: 84,
    height: 84,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: fpColors.surfaceContainerLow,
  },
  itemImage: { width: '100%', height: '100%' },
  itemImageFallback: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemBody: { flex: 1, gap: 2 },
  itemKicker: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.onSurfaceVariant,
  },
  itemName: {
    fontFamily: 'Inter_800ExtraBold',
    fontSize: 18,
    lineHeight: 22,
    color: fpColors.onSurface,
    letterSpacing: -0.2,
  },
  itemLocation: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 4,
  },
  itemLocationText: {
    fontFamily: 'Inter_600SemiBold',
    fontSize: 13,
    color: fpColors.primary,
  },
  section: { gap: 10 },
  sectionLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  helperText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 13,
    color: fpColors.onSurfaceVariant,
    marginTop: 10,
    lineHeight: 18,
  },
  emailInputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: `${fpColors.primary}0a`,
  },
  emailInput: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 15,
    color: fpColors.onSurface,
    ...(({ outlineStyle: 'none', outlineWidth: 0 } as any)),
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    padding: 12,
    borderRadius: 10,
    backgroundColor: fpColors.errorContainer,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 13,
    color: fpColors.onErrorContainer,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLowest,
  },
  cancelBtn: {
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cancelLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 15,
    color: fpColors.primary,
  },
});
